<template>
  <div class="lead-distribution" :class="{ 'lead-distribution--dark': isDarkSkin }">
    <b-overlay :show="loading" rounded="lg" opacity="0.45" spinner-variant="primary">

      <!-- ============ HEADER ============ -->
      <div class="ld-header">
        <div class="ld-header__icon"><i class="fa-solid fa-shuffle"></i></div>
        <div class="ld-header__text">
          <h3 class="ld-header__title">{{ $t('leadDistribution.title') }}</h3>
          <p class="ld-header__subtitle">{{ $t('leadDistribution.subtitle') }}</p>
        </div>
        <span v-if="!pristine && !loading" class="ld-dirty">
          <i class="fa-solid fa-circle-exclamation mr-25"></i>{{ $t('leadDistribution.unsavedChanges') }}
        </span>
      </div>

      <!-- ============ MODE PICKER ============ -->
      <div class="ld-modes-label">{{ $t('leadDistribution.modeTitle') }}</div>
      <div class="ld-modes">
        <button type="button" class="ld-mode" :class="{ 'is-selected': form.mode === 'RANDOM' }"
                @click="setMode('RANDOM')">
          <div class="ld-mode__icon ld-mode__icon--random"><i class="fa-solid fa-dice"></i></div>
          <div class="ld-mode__body">
            <div class="ld-mode__name">{{ $t('leadDistribution.modeRandom') }}</div>
            <div class="ld-mode__desc">{{ $t('leadDistribution.modeRandomDesc') }}</div>
          </div>
          <div class="ld-mode__check"><i class="fa-solid fa-circle-check"></i></div>
        </button>
        <button type="button" class="ld-mode" :class="{ 'is-selected': form.mode === 'ROUND_ROBIN' }"
                @click="setMode('ROUND_ROBIN')">
          <div class="ld-mode__icon ld-mode__icon--rr"><i class="fa-solid fa-rotate"></i></div>
          <div class="ld-mode__body">
            <div class="ld-mode__name">{{ $t('leadDistribution.modeRoundRobin') }}</div>
            <div class="ld-mode__desc">{{ $t('leadDistribution.modeRoundRobinDesc') }}</div>
          </div>
          <div class="ld-mode__check"><i class="fa-solid fa-circle-check"></i></div>
        </button>
      </div>

      <!-- ============ FLOW CLOSED BANNER ============ -->
      <div v-if="flowClosed && !loading" class="ld-banner">
        <i class="fa-solid fa-triangle-exclamation ld-banner__icon"></i>
        <div>
          <div class="ld-banner__title">{{ $t('leadDistribution.closedBannerTitle') }}</div>
          <div class="ld-banner__text">{{ $t('leadDistribution.closedBannerText') }}</div>
        </div>
      </div>

      <!-- ============ TARGETS ============ -->
      <b-card no-body class="ld-card">
        <b-card-header class="ld-card__header">
          <div>
            <h5 class="mb-0">{{ $t('leadDistribution.targets') }}</h5>
            <small class="ld-card__hint">{{ $t('leadDistribution.targetsHint') }}</small>
          </div>
          <div class="ld-card__meta">
            <span class="ld-meta-chip">{{ $t('leadDistribution.targetsChip', { count: form.entries.length }) }}</span>
            <span class="ld-meta-chip ld-meta-chip--open">{{ $t('leadDistribution.openChip', { count: enabledEntries.length }) }}</span>
            <span v-if="totalWeight > 0" class="ld-meta-chip ld-meta-chip--weight">
              {{ (isRoundRobin ? $t('leadDistribution.cycleLength') : $t('leadDistribution.totalWeight')) + ': ' + totalWeight }}
            </span>
            <b-button variant="primary" size="sm" @click="addEntry">
              <i class="fa-solid fa-plus mr-25"></i>{{ $t('leadDistribution.addEntry') }}
            </b-button>
          </div>
        </b-card-header>
        <b-card-body>

          <!-- column headers (desktop) -->
          <div v-if="form.entries.length" class="ld-head">
            <div></div>
            <div>{{ $t('leadDistribution.organization') }}</div>
            <div>{{ $t('leadDistribution.businessUnit') }}</div>
            <div>{{ weightLabel }}</div>
            <div>{{ $t('leadDistribution.share') }}</div>
            <div>{{ $t('leadDistribution.status') }}</div>
            <div></div>
          </div>

          <!-- one row per target -->
          <div v-for="(entry, index) in form.entries" :key="entry._key"
               class="ld-row"
               :class="{ 'ld-row--closed': !entry.enabled, 'ld-row--dup': duplicateKeys.has(entryKey(entry)) }">
            <div class="ld-row__num" :style="{ background: entryColor(index) }">{{ index + 1 }}</div>

            <div class="ld-cell ld-cell--org">
              <label class="ld-cell__label">{{ $t('leadDistribution.organization') }}</label>
              <organization-select-box v-model="entry.orgId" :clearable="false"
                                       :placeholder="$t('leadDistribution.selectOrganization')"
                                       @input="onOrgChanged(entry)"/>
            </div>

            <div class="ld-cell ld-cell--bu">
              <label class="ld-cell__label">{{ $t('leadDistribution.businessUnit') }}</label>
              <v-select v-model="entry.businessUnitId" :options="businessUnitOptions(entry)"
                        label="name" :reduce="bu => bu.id" :clearable="false"
                        :disabled="!entry.orgId"
                        :placeholder="$t('leadDistribution.selectBusinessUnit')">
                <template v-slot:option="bu">
                  <span><span :class="bu.type"></span>{{ bu.name + ' - ' + bu.type }}</span>
                </template>
              </v-select>
            </div>

            <div class="ld-cell ld-cell--weight">
              <label class="ld-cell__label">{{ weightLabel }}</label>
              <b-form-input type="number" min="1" step="1" v-model.number="entry.weight"
                            :state="validWeight(entry) ? null : false"/>
            </div>

            <div class="ld-cell ld-cell--share">
              <label class="ld-cell__label">{{ $t('leadDistribution.share') }}</label>
              <div class="ld-share">
                <span class="ld-share__pct">{{ sharePercentLabel(entry) }}</span>
                <div class="ld-share__track">
                  <div class="ld-share__fill" :style="{ width: shareWidth(entry), background: entryColor(index) }"></div>
                </div>
              </div>
            </div>

            <div class="ld-cell ld-cell--status">
              <label class="ld-cell__label">{{ $t('leadDistribution.status') }}</label>
              <div class="ld-status">
                <b-form-checkbox switch v-model="entry.enabled"/>
                <span class="ld-pill" :class="entry.enabled ? 'ld-pill--open' : 'ld-pill--closed'">
                  <i :class="entry.enabled ? 'fa-solid fa-circle-check' : 'fa-solid fa-lock'" class="mr-25"></i>
                  {{ entry.enabled ? $t('leadDistribution.open') : $t('leadDistribution.closed') }}
                </span>
              </div>
            </div>

            <b-button variant="flat-danger" class="btn-icon ld-remove" v-b-tooltip
                      :title="$t('leadDistribution.removeEntry')" @click="removeEntry(index)">
              <i class="fa-solid fa-trash-can"></i>
            </b-button>
          </div>

          <!-- empty state -->
          <div v-if="!form.entries.length && !loading" class="ld-empty">
            <i class="fa-solid fa-shuffle ld-empty__icon"></i>
            <div class="ld-empty__title">{{ $t('leadDistribution.emptyTitle') }}</div>
            <div class="ld-empty__hint">{{ $t('leadDistribution.emptyHint') }}</div>
            <b-button variant="primary" size="sm" @click="addEntry">
              <i class="fa-solid fa-plus mr-25"></i>{{ $t('leadDistribution.addEntry') }}
            </b-button>
          </div>

          <!-- inline validation warnings -->
          <div v-if="validationMessages.length" class="ld-problems">
            <div v-for="msg in validationMessages" :key="msg" class="ld-problems__item">
              <i class="fa-solid fa-circle-exclamation mr-25"></i>{{ msg }}
            </div>
          </div>

          <!-- rotation preview (round robin only) -->
          <div v-if="isRoundRobin && rotationChips.length" class="ld-rotation">
            <div class="ld-rotation__title">
              <i class="fa-solid fa-rotate mr-25"></i>{{ $t('leadDistribution.rotationPreview') }}
            </div>
            <div class="ld-rotation__strip">
              <span v-for="(chip, i) in rotationChips" :key="i" class="ld-chip"
                    :style="{ background: chip.color }">{{ chip.label }}</span>
              <span v-if="rotationOverflow" class="ld-chip ld-chip--more">&hellip;</span>
              <span class="ld-rotation__repeat">
                <i class="fa-solid fa-rotate-right mr-25"></i>{{ $t('leadDistribution.repeats') }}
              </span>
            </div>
            <small class="ld-rotation__hint">{{ $t('leadDistribution.rotationHint') }}</small>
          </div>

        </b-card-body>
      </b-card>

      <!-- ============ FOOTER / SAVE BAR ============ -->
      <div class="ld-footer">
        <div class="ld-footer__meta">
          <template v-if="lastUpdateTime">
            <i class="fa-regular fa-clock mr-25"></i>
            {{ $t('leadDistribution.lastUpdated', { time: lastUpdateTime }) }}
            <span v-if="lastUpdateBy">{{ ' ' + $t('leadDistribution.updatedBy', { name: lastUpdateBy }) }}</span>
          </template>
        </div>
        <div class="ld-footer__actions">
          <b-button variant="outline-secondary" :disabled="pristine || saving" @click="resetForm">
            {{ $t('leadDistribution.discard') }}
          </b-button>
          <b-button variant="primary" :disabled="!canSave" @click="save">
            <b-spinner v-if="saving" small class="mr-50"/>
            <i v-else class="fa-solid fa-floppy-disk mr-50"></i>
            {{ $t('leadDistribution.save') }}
          </b-button>
        </div>
      </div>

    </b-overlay>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import mixinBase from '@/components/mixin/mixinBase';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import mixinPermissions from '@/components/mixin/mixinPermissions';
import OrganizationSelectBox from '@/components/widget/select/OrganizationSelectBox.vue';

const MODE_RANDOM = 'RANDOM';
const MODE_ROUND_ROBIN = 'ROUND_ROBIN';
const ROTATION_PREVIEW_CAP = 20;
// Same categorical palette used across the dashboards (one color per row,
// reused for its share bar and its chips in the rotation preview)
const ENTRY_PALETTE = ['#7367f0', '#28c76f', '#00cfe8', '#ff9f43', '#ea5455', '#e83e8c', '#3fbff7', '#9e95f5'];

let entryUid = 0;

export default {
  name: 'LeadDistributionView',
  components: { OrganizationSelectBox },
  mixins: [mixinBase, mixinNotifications, mixinPermissions],
  data: () => ({
    loading: true,
    saving: false,
    // Raw server object — keeps updateTime / updatedByUserId for the footer
    original: null,
    // Serialized snapshot of the form as loaded/saved — pristine detection
    snapshot: null,
    form: { mode: MODE_RANDOM, entries: [] },
  }),
  computed: {
    ...mapGetters('appConfig', ['isDarkSkin']),
    ...mapGetters('data', ['allBusinessUnits', 'getOrganizationNameById', 'getBusinessUnitNameById', 'getUserFullName']),
    isRoundRobin() { return this.form.mode === MODE_ROUND_ROBIN; },
    weightLabel() {
      return this.isRoundRobin ? this.$t('leadDistribution.stepsPerCycle') : this.$t('leadDistribution.weight');
    },
    enabledEntries() { return this.form.entries.filter(e => e.enabled); },
    // Shares are computed over OPEN entries only — closed targets take no leads
    totalWeight() {
      return this.enabledEntries.reduce((sum, e) => sum + (this.validWeight(e) ? e.weight : 0), 0);
    },
    flowClosed() { return this.enabledEntries.length === 0; },
    // org+BU pairs that appear more than once (only complete pairs are compared)
    duplicateKeys() {
      const seen = new Set();
      const dup = new Set();
      this.form.entries.forEach(e => {
        if (!e.orgId || !e.businessUnitId) return;
        const key = this.entryKey(e);
        if (seen.has(key)) dup.add(key);
        else seen.add(key);
      });
      return dup;
    },
    validationMessages() {
      const messages = [];
      if (this.form.entries.some(e => !e.orgId || !e.businessUnitId)) {
        messages.push(this.$t('leadDistribution.incompleteWarning'));
      }
      if (this.form.entries.some(e => !this.validWeight(e))) {
        messages.push(this.$t('leadDistribution.weightWarning'));
      }
      if (this.duplicateKeys.size > 0) {
        messages.push(this.$t('leadDistribution.duplicateWarning'));
      }
      return messages;
    },
    pristine() { return this.snapshot === this.serializeForm(); },
    // Closing the whole flow is a legit action, so flowClosed does NOT block saving
    canSave() {
      return !this.pristine && !this.saving && !this.loading && this.validationMessages.length === 0;
    },
    // Dealing order: enabled entries in row order, each repeated `weight` times,
    // capped at ROTATION_PREVIEW_CAP chips
    rotation() {
      const chips = [];
      let overflow = false;
      for (const entry of this.enabledEntries) {
        if (!entry.orgId || !entry.businessUnitId || !this.validWeight(entry)) continue;
        const label = this.entryLabel(entry);
        const color = this.entryColor(this.form.entries.indexOf(entry));
        const steps = Math.max(1, Math.floor(entry.weight));
        for (let i = 0; i < steps; i++) {
          if (chips.length >= ROTATION_PREVIEW_CAP) {
            overflow = true;
            break;
          }
          chips.push({ label, color });
        }
        if (overflow) break;
      }
      return { chips, overflow };
    },
    rotationChips() { return this.rotation.chips; },
    rotationOverflow() { return this.rotation.overflow; },
    lastUpdateTime() {
      if (!this.original || !this.original.updateTime) return null;
      return this.$moment(this.original.updateTime).format('DD/MM/YYYY HH:mm');
    },
    lastUpdateBy() {
      if (!this.original || this.original.updatedByUserId == null) return null;
      return this.getUserFullName(this.original.updatedByUserId);
    },
  },
  created() {
    this.refresh();
  },
  methods: {
    ...mapActions('leadDistribution', ['getLeadDistribution', 'updateLeadDistribution']),
    refresh() {
      this.loading = true;
      this.getLeadDistribution()
          .then(data => this.applyData(data))
          .catch(err => this.showServerErrorNotification('fetchDataError', 'leadDistribution', err))
          .finally(() => { this.loading = false; });
    },
    applyData(data) {
      const config = data || {};
      this.original = config;
      this.form = {
        mode: config.mode === MODE_ROUND_ROBIN ? MODE_ROUND_ROBIN : MODE_RANDOM,
        entries: (config.entries || []).map(entry => ({
          _key: ++entryUid,
          orgId: entry.orgId || null,
          businessUnitId: entry.businessUnitId || null,
          weight: entry.weight != null ? entry.weight : 1,
          enabled: entry.enabled !== false,
        })),
      };
      this.snapshot = this.serializeForm();
    },
    serializeForm() {
      return JSON.stringify({
        mode: this.form.mode,
        entries: this.form.entries.map(e => ({
          orgId: e.orgId || null,
          businessUnitId: e.businessUnitId || null,
          weight: e.weight,
          enabled: !!e.enabled,
        })),
      });
    },
    save() {
      if (!this.canSave) return;
      this.saving = true;
      this.updateLeadDistribution({
        mode: this.form.mode,
        entries: this.form.entries.map(e => ({
          orgId: e.orgId,
          businessUnitId: e.businessUnitId,
          weight: e.weight,
          enabled: !!e.enabled,
        })),
      })
          .then(data => {
            this.applyData(data);
            this.showServerSuccessNotification('updateDataSuccess', 'leadDistribution');
          })
          .catch(err => this.showServerErrorNotification('updateDataError', 'leadDistribution', err))
          .finally(() => { this.saving = false; });
    },
    resetForm() {
      this.applyData(this.original);
    },
    setMode(mode) {
      this.form.mode = mode;
    },
    addEntry() {
      this.form.entries.push({
        _key: ++entryUid,
        orgId: null,
        businessUnitId: null,
        weight: 1,
        enabled: true,
      });
    },
    removeEntry(index) {
      this.form.entries.splice(index, 1);
    },
    // Switching organization invalidates a business unit of another org
    onOrgChanged(entry) {
      if (!entry.businessUnitId) return;
      const bu = (this.allBusinessUnits || []).find(b => b.id === entry.businessUnitId);
      if (!bu || bu.orgId !== entry.orgId) {
        entry.businessUnitId = null;
      }
    },
    businessUnitOptions(entry) {
      if (!entry.orgId) return [];
      return (this.allBusinessUnits || []).filter(bu => bu.orgId === entry.orgId);
    },
    entryKey(entry) {
      return `${entry.orgId}|${entry.businessUnitId}`;
    },
    entryLabel(entry) {
      return `${this.getOrganizationNameById(entry.orgId) || '?'} / ${this.getBusinessUnitNameById(entry.businessUnitId) || '?'}`;
    },
    entryColor(index) {
      return ENTRY_PALETTE[((index % ENTRY_PALETTE.length) + ENTRY_PALETTE.length) % ENTRY_PALETTE.length];
    },
    validWeight(entry) {
      return typeof entry.weight === 'number' && !isNaN(entry.weight) && entry.weight >= 1;
    },
    sharePercent(entry) {
      if (!entry.enabled || !this.validWeight(entry) || this.totalWeight <= 0) return 0;
      return (entry.weight / this.totalWeight) * 100;
    },
    sharePercentLabel(entry) {
      if (!entry.enabled) return '—';
      return `${Math.round(this.sharePercent(entry) * 10) / 10}%`;
    },
    shareWidth(entry) {
      const pct = this.sharePercent(entry);
      if (pct <= 0) return '0%';
      return `${Math.max(3, Math.round(pct))}%`;
    },
  },
};
</script>

<style scoped lang="scss">
$ld-text: #5e5873;
$ld-muted: #9a9dad;
$ld-primary: #7367f0;

/* Row / head grid: # | org | BU | weight | share | status | remove */
$ld-columns: 36px minmax(190px, 1.25fr) minmax(190px, 1.25fr) 120px minmax(150px, 1fr) 140px 40px;

/* ===== Header ===== */
.ld-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.25rem;

  &__icon {
    width: 54px; height: 54px; border-radius: 16px; flex-shrink: 0;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 1.4rem; color: #fff;
    background: linear-gradient(135deg, #7367f0 0%, #9e95f5 100%);
    box-shadow: 0 10px 22px -12px rgba(115, 103, 240, 0.7);
  }
  &__text { min-width: 0; }
  &__title { margin-bottom: 0.15rem; font-weight: 800; color: $ld-text; }
  &__subtitle { margin-bottom: 0; font-size: 0.85rem; color: $ld-muted; }
}

.ld-dirty {
  margin-left: auto; flex-shrink: 0;
  display: inline-flex; align-items: center;
  padding: 0.35rem 0.8rem; border-radius: 999px;
  background: rgba(255, 159, 67, 0.14);
  border: 1px solid rgba(255, 159, 67, 0.4);
  color: #e87722; font-size: 0.75rem; font-weight: 800;
  white-space: nowrap;
}

/* ===== Mode picker ===== */
.ld-modes-label {
  font-size: 0.78rem; font-weight: 800; text-transform: uppercase;
  letter-spacing: 0.08em; color: $ld-muted; margin-bottom: 0.5rem;
}

.ld-modes {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.ld-mode {
  display: flex; align-items: center; gap: 1rem; text-align: left;
  border-radius: 16px; padding: 1.1rem 1.25rem;
  background: #fff;
  border: 2px solid rgba(110, 117, 159, 0.14);
  cursor: pointer; outline: none;
  transition: all 0.18s ease;

  &:hover {
    border-color: rgba(115, 103, 240, 0.45);
    transform: translateY(-2px);
    box-shadow: 0 10px 24px -14px rgba(115, 103, 240, 0.4);
  }

  &__icon {
    width: 52px; height: 52px; border-radius: 14px; flex-shrink: 0;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 1.35rem; color: #fff;

    &--random { background: linear-gradient(135deg, #7367f0, #9e95f5); }
    &--rr { background: linear-gradient(135deg, #00cfe8, #1f9dea); }
  }
  &__body { min-width: 0; }
  &__name { font-weight: 800; font-size: 1.05rem; color: $ld-text; }
  &__desc { font-size: 0.8rem; color: $ld-muted; margin-top: 2px; }
  &__check {
    margin-left: auto; font-size: 1.35rem;
    color: rgba(110, 117, 159, 0.22);
    transition: color 0.18s ease;
  }

  &.is-selected {
    border-color: $ld-primary;
    background: linear-gradient(135deg, rgba(115, 103, 240, 0.09), rgba(115, 103, 240, 0.02));
    box-shadow: 0 12px 26px -16px rgba(115, 103, 240, 0.55);

    .ld-mode__check { color: #28c76f; }
  }
}

/* ===== Flow closed banner ===== */
.ld-banner {
  display: flex; align-items: flex-start; gap: 0.9rem;
  border-radius: 14px; padding: 1rem 1.15rem; margin-bottom: 1.25rem;
  background: linear-gradient(135deg, rgba(255, 159, 67, 0.16), rgba(234, 84, 85, 0.14));
  border: 1px solid rgba(234, 84, 85, 0.35);

  &__icon { font-size: 1.5rem; color: #ea5455; margin-top: 2px; }
  &__title { font-weight: 800; color: #ea5455; }
  &__text { font-size: 0.85rem; color: #6e6b7b; margin-top: 2px; }
}

/* ===== Targets card ===== */
.ld-card {
  border-radius: 16px;

  &__header {
    display: flex; align-items: center; justify-content: space-between;
    gap: 0.75rem; flex-wrap: wrap;

    h5 { font-weight: 800; }
  }
  &__hint { color: $ld-muted; }
  &__meta { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
}

.ld-meta-chip {
  display: inline-flex; align-items: center;
  padding: 0.28rem 0.7rem; border-radius: 999px;
  background: rgba(110, 117, 159, 0.1);
  color: #6e6b7b; font-size: 0.72rem; font-weight: 800;
  white-space: nowrap;

  &--open { background: rgba(40, 199, 111, 0.12); color: #28c76f; }
  &--weight { background: rgba(115, 103, 240, 0.12); color: $ld-primary; }
}

/* ===== Column headers ===== */
.ld-head {
  display: grid;
  grid-template-columns: $ld-columns;
  gap: 0 0.8rem;
  padding: 0 0.9rem 0.45rem;

  > div {
    font-size: 0.65rem; font-weight: 800; text-transform: uppercase;
    letter-spacing: 0.08em; color: $ld-muted; white-space: nowrap;
    overflow: hidden; text-overflow: ellipsis;
  }
}

/* ===== Entry rows ===== */
.ld-row {
  display: grid;
  grid-template-columns: $ld-columns;
  gap: 0.8rem;
  align-items: center;
  background: rgba(110, 117, 159, 0.04);
  border: 1px solid rgba(110, 117, 159, 0.12);
  border-radius: 14px;
  padding: 0.75rem 0.9rem;
  margin-bottom: 0.6rem;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;

  &:hover { border-color: rgba(115, 103, 240, 0.35); }

  &__num {
    width: 28px; height: 28px; border-radius: 9px;
    display: inline-flex; align-items: center; justify-content: center;
    color: #fff; font-weight: 800; font-size: 0.75rem;
    box-shadow: 0 4px 10px -5px rgba(20, 24, 50, 0.45);
  }

  /* Closed target: greyed out, but the status cell stays fully readable */
  &--closed {
    background: rgba(110, 117, 159, 0.09);
    border-style: dashed;

    .ld-cell:not(.ld-cell--status),
    .ld-row__num { opacity: 0.5; }
  }

  /* Duplicate org+BU pair */
  &--dup {
    border-color: rgba(234, 84, 85, 0.55);
    background: rgba(234, 84, 85, 0.06);
  }
}

.ld-cell {
  min-width: 0;

  &__label {
    display: none;
    font-size: 0.62rem; font-weight: 800; text-transform: uppercase;
    letter-spacing: 0.08em; color: $ld-muted; margin-bottom: 0.2rem;
  }
}

/* ===== Live share bar ===== */
.ld-share {
  display: flex; flex-direction: column; gap: 4px;

  &__pct {
    font-weight: 800; font-size: 0.9rem; color: $ld-text;
    font-variant-numeric: tabular-nums;
  }
  &__track {
    height: 8px; border-radius: 999px;
    background: rgba(110, 117, 159, 0.14); overflow: hidden;
  }
  &__fill { height: 100%; border-radius: 999px; transition: width 0.35s ease; }
}

/* ===== Status toggle + pill ===== */
.ld-status { display: flex; align-items: center; gap: 0.25rem; }

.ld-pill {
  display: inline-flex; align-items: center;
  padding: 0.22rem 0.6rem; border-radius: 999px;
  font-size: 0.68rem; font-weight: 800; text-transform: uppercase;
  letter-spacing: 0.04em; white-space: nowrap;

  &--open { background: rgba(40, 199, 111, 0.12); color: #28c76f; }
  &--closed { background: rgba(234, 84, 85, 0.12); color: #ea5455; }
}

.ld-remove { justify-self: end; }

/* ===== Empty state ===== */
.ld-empty {
  text-align: center; padding: 2.5rem 1rem; color: $ld-muted;

  &__icon { font-size: 2.2rem; opacity: 0.45; }
  &__title { font-weight: 700; margin-top: 0.7rem; font-size: 1rem; color: $ld-text; }
  &__hint { font-size: 0.82rem; margin: 0.2rem 0 1rem; }
}

/* ===== Inline validation problems ===== */
.ld-problems {
  margin-top: 0.4rem;

  &__item {
    display: flex; align-items: center;
    font-size: 0.8rem; font-weight: 600; color: #ea5455;
    padding: 0.25rem 0.2rem;
  }
}

/* ===== Rotation preview ===== */
.ld-rotation {
  margin-top: 1rem; padding-top: 1rem;
  border-top: 1px dashed rgba(110, 117, 159, 0.25);

  &__title {
    font-size: 0.78rem; font-weight: 800; text-transform: uppercase;
    letter-spacing: 0.08em; color: $ld-text; margin-bottom: 0.6rem;
  }
  &__strip { display: flex; align-items: center; flex-wrap: wrap; gap: 0.4rem; }
  &__repeat {
    font-size: 0.75rem; font-weight: 700; color: $ld-muted;
    margin-left: 0.3rem; white-space: nowrap;
  }
  &__hint { display: block; margin-top: 0.5rem; color: $ld-muted; }
}

.ld-chip {
  display: inline-flex; align-items: center;
  padding: 0.3rem 0.65rem; border-radius: 999px;
  color: #fff; font-size: 0.72rem; font-weight: 700; white-space: nowrap;
  box-shadow: 0 4px 10px -6px rgba(20, 24, 50, 0.4);

  &--more {
    background: rgba(110, 117, 159, 0.25); color: #6e6b7b;
    font-weight: 900; letter-spacing: 2px; box-shadow: none;
  }
}

/* ===== Footer / save bar ===== */
.ld-footer {
  display: flex; align-items: center; justify-content: space-between;
  gap: 1rem; flex-wrap: wrap; margin-top: 0.25rem;

  &__meta { font-size: 0.8rem; color: $ld-muted; }
  &__actions { display: flex; gap: 0.6rem; margin-left: auto; }
}

/* ===== Dark skin ===== */
.lead-distribution--dark {
  .ld-header__title { color: #d0d2d6; }

  .ld-mode {
    background: #283046;
    border-color: rgba(255, 255, 255, 0.08);

    &__name { color: #d0d2d6; }
    &__check { color: rgba(255, 255, 255, 0.12); }

    &.is-selected {
      border-color: $ld-primary;
      background: linear-gradient(135deg, rgba(115, 103, 240, 0.18), rgba(115, 103, 240, 0.05));

      .ld-mode__check { color: #28c76f; }
    }
  }

  .ld-banner__text { color: #d0d2d6; }

  .ld-row {
    background: rgba(255, 255, 255, 0.03);
    border-color: rgba(255, 255, 255, 0.08);

    &--closed { background: rgba(255, 255, 255, 0.015); }
    &--dup {
      border-color: rgba(234, 84, 85, 0.55);
      background: rgba(234, 84, 85, 0.1);
    }
  }

  .ld-share__pct { color: #d0d2d6; }
  .ld-share__track { background: rgba(255, 255, 255, 0.1); }

  .ld-meta-chip { background: rgba(255, 255, 255, 0.07); color: #b4b7bd;
    &--open { background: rgba(40, 199, 111, 0.16); color: #48da8c; }
    &--weight { background: rgba(115, 103, 240, 0.2); color: #9e95f5; }
  }

  .ld-rotation { border-top-color: rgba(255, 255, 255, 0.12); }
  .ld-rotation__title { color: #d0d2d6; }
  .ld-chip--more { background: rgba(255, 255, 255, 0.12); color: #b4b7bd; }

  .ld-empty__title { color: #d0d2d6; }
}

/* ===== Responsive: rows become stacked mini-cards ===== */
@media (max-width: 1100px) {
  .ld-modes { grid-template-columns: 1fr; }

  .ld-head { display: none; }

  .ld-row {
    grid-template-columns: 36px 1fr 1fr 40px;
    grid-template-areas:
      'num org org remove'
      '.   bu  bu  .'
      '.   weight share .'
      '.   status status .';
    row-gap: 0.7rem;
  }

  .ld-row__num { grid-area: num; }
  .ld-cell--org { grid-area: org; }
  .ld-cell--bu { grid-area: bu; }
  .ld-cell--weight { grid-area: weight; }
  .ld-cell--share { grid-area: share; }
  .ld-cell--status { grid-area: status; }
  .ld-remove { grid-area: remove; }

  .ld-cell__label { display: block; }
}
</style>
